import React, { useState, useRef, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import apiService from '../../services/api';

// Cache previews in memory across component instances
const audioCache = new Map();

/**
 * VoicePreviewButton — Plays a voice preview sample using Gemini Live API.
 * Reusable across admin settings, therapist settings, and persona management.
 */
const VoicePreviewButton = ({ voiceName, sampleText, token, size = 'small', accentPresetId = null }) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);

  const stopPlayback = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (e) {}
      sourceRef.current = null;
    }
    setPlaying(false);
  }, []);

  const handleClick = useCallback(async () => {
    if (playing) {
      stopPlayback();
      return;
    }

    const cacheKey = `${voiceName}:${accentPresetId || 'default'}:${sampleText || 'default'}`;

    try {
      let audioData;
      let mimeType = 'audio/pcm;rate=24000';

      // Check local cache first
      if (audioCache.has(cacheKey)) {
        const cached = audioCache.get(cacheKey);
        audioData = cached.audioData;
        mimeType = cached.mimeType || mimeType;
      } else {
        setLoading(true);
        const result = await apiService.previewVoice(token, voiceName, sampleText, accentPresetId);
        audioData = result.audioData;
        mimeType = result.mimeType || mimeType;
        if (audioData) {
          audioCache.set(cacheKey, { audioData, mimeType });
        }
        setLoading(false);
      }

      if (!audioData) return;

      // Decode base64 to raw bytes
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Gemini always returns raw PCM 16-bit little-endian
      const rateMatch = mimeType.match(/rate=(\d+)/);
      const sampleRate = rateMatch ? parseInt(rateMatch[1]) : 24000;

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate });
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') await ctx.resume();

      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768;
      }
      const buffer = ctx.createBuffer(1, float32.length, sampleRate);
      buffer.getChannelData(0).set(float32);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.onended = () => setPlaying(false);
      sourceRef.current = source;

      setPlaying(true);
      source.start();
    } catch (err) {
      console.error('[VoicePreview] Error:', err);
      setLoading(false);
      setPlaying(false);
    }
  }, [voiceName, sampleText, token, playing, stopPlayback]);

  if (loading) {
    return <CircularProgress size={size === 'small' ? 18 : 24} />;
  }

  return (
    <Tooltip title={playing ? 'Stop preview' : `Preview ${voiceName}`}>
      <IconButton onClick={handleClick} size={size} color={playing ? 'error' : 'primary'}>
        {playing ? <StopIcon fontSize={size} /> : <PlayArrowIcon fontSize={size} />}
      </IconButton>
    </Tooltip>
  );
};

export default VoicePreviewButton;
